import { useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Lead, Stage } from "@/types/lead";
import {
  downloadLeadsXlsx,
  fetchLeadsForExport,
  fetchWorkspaceLeadCount,
  type LeadsExportScope,
} from "@/lib/leads-export";

interface ExportLeadsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  dataMode: "funnels" | "groups";
  activeFunnelId: string | null;
  activeFunnelName?: string;
  isIntegrationFunnel: boolean;
  isDefaultFunnel: boolean;
  selectedGroupId: string | null;
  selectedGroupName?: string;
  stages: Stage[];
  debouncedSearchTerm: string;
  selectedStage: string;
  selectedTags: string[];
  sortOption: string;
  selectedLeadIds: Set<string>;
  displayLeadTotal: number;
  loadedLeadCount: number;
}

export function ExportLeadsDialog({
  open,
  onOpenChange,
  workspaceId,
  dataMode,
  activeFunnelId,
  activeFunnelName,
  isIntegrationFunnel,
  isDefaultFunnel,
  selectedGroupId,
  selectedGroupName,
  stages,
  debouncedSearchTerm,
  selectedStage,
  selectedTags,
  sortOption,
  selectedLeadIds,
  displayLeadTotal,
  loadedLeadCount,
}: ExportLeadsDialogProps) {
  const { toast } = useToast();
  const [scope, setScope] = useState<LeadsExportScope>("all_funnel");
  const [exportStageSlug, setExportStageSlug] = useState<string>(stages[0]?.slug ?? "");
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [workspaceLeadTotal, setWorkspaceLeadTotal] = useState<number | null>(null);

  const selectedCount = selectedLeadIds.size;
  const hasActiveFilters = Boolean(
    debouncedSearchTerm.trim() || selectedStage !== "all" || selectedTags.length > 0,
  );

  useEffect(() => {
    if (!open) return;
    if (selectedCount > 0) {
      setScope("selected");
    } else if (hasActiveFilters) {
      setScope("current_filter");
    } else {
      setScope("all_funnel");
    }
    if (stages[0]?.slug) setExportStageSlug(stages[0].slug);

    void fetchWorkspaceLeadCount(supabase, workspaceId)
      .then(setWorkspaceLeadTotal)
      .catch(() => setWorkspaceLeadTotal(null));
  }, [open, selectedCount, hasActiveFilters, stages, workspaceId]);

  const estimatedCount = useMemo(() => {
    if (scope === "selected") return selectedCount;
    if (scope === "all_workspace") return workspaceLeadTotal ?? undefined;
    if (scope === "current_filter") return loadedLeadCount;
    if (scope === "stage") return undefined;
    if (dataMode === "groups") return displayLeadTotal;
    return displayLeadTotal;
  }, [scope, selectedCount, workspaceLeadTotal, loadedLeadCount, displayLeadTotal, dataMode]);

  const handleExport = async () => {
    if (
      scope !== "all_workspace" &&
      scope !== "selected" &&
      dataMode === "funnels" &&
      !activeFunnelId
    ) {
      toast({
        title: "Selecione um funil",
        description: "Escolha um funil antes de exportar.",
        variant: "destructive",
      });
      return;
    }
    if (scope !== "all_workspace" && dataMode === "groups" && !selectedGroupId) {
      toast({
        title: "Selecione um grupo",
        description: "Escolha um grupo antes de exportar.",
        variant: "destructive",
      });
      return;
    }
    if (scope === "stage" && !exportStageSlug) {
      toast({
        title: "Selecione uma coluna",
        description: "Escolha o estágio do funil para exportar.",
        variant: "destructive",
      });
      return;
    }

    setExporting(true);
    setProgress(0);
    try {
      const leads = await fetchLeadsForExport(
        supabase,
        {
          workspaceId,
          dataMode,
          activeFunnelId,
          isIntegrationFunnel,
          isDefaultFunnel,
          selectedGroupId,
          search: debouncedSearchTerm,
          stageFilter: selectedStage,
          tagIds: selectedTags,
          sortOption,
          selectedLeadIds: [...selectedLeadIds],
          scope,
          exportStageSlug: scope === "stage" ? exportStageSlug : null,
        },
        setProgress,
      );

      if (leads.length === 0) {
        toast({
          title: "Nenhum lead para exportar",
          description: "Ajuste os filtros ou o escopo da exportação.",
          variant: "destructive",
        });
        return;
      }

      const prefix =
        scope === "all_workspace"
          ? "workspace_leads"
          : dataMode === "groups"
            ? `grupo_${selectedGroupName || "leads"}`
            : `funil_${activeFunnelName || "leads"}`;

      const count = downloadLeadsXlsx(leads as Lead[], prefix.replace(/\s+/g, "_"));
      toast({
        title: "Exportação concluída",
        description: `${count} leads exportados com sucesso.`,
      });
      onOpenChange(false);
    } catch (e: unknown) {
      const err = e as { message?: string; details?: string; hint?: string; code?: string } | null;
      const message =
        (e instanceof Error && e.message) ||
        err?.message ||
        err?.details ||
        err?.hint ||
        (err?.code ? `Código ${err.code}` : "") ||
        (typeof e === "string" ? e : "") ||
        "Erro desconhecido";
      console.error("[leads-export] failed", e);
      toast({
        title: "Erro na exportação",
        description: message,
        variant: "destructive",
      });
    } finally {
      setExporting(false);
      setProgress(0);
    }
  };

  const funnelLabel = activeFunnelName || "funil atual";
  const groupLabel = selectedGroupName || "grupo atual";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Exportar leads</DialogTitle>
          <DialogDescription>
            Os leads são buscados no servidor — não apenas os carregados na tela.
            {dataMode === "funnels" && displayLeadTotal > 0 && (
              <> Total no {funnelLabel}: <strong>{displayLeadTotal}</strong>.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={scope}
          onValueChange={(v) => setScope(v as LeadsExportScope)}
          className="space-y-3"
        >
          <div className="flex items-start gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <RadioGroupItem value="all_workspace" id="exp-workspace" className="mt-0.5" />
            <Label htmlFor="exp-workspace" className="cursor-pointer font-normal leading-snug">
              <span className="font-medium">Todos do workspace</span>
              <span className="block text-xs text-muted-foreground">
                Exporta todos os {workspaceLeadTotal ?? "…"} leads do workspace (todos os funis)
              </span>
            </Label>
          </div>

          {dataMode === "funnels" && (
            <>
              <div className="flex items-start gap-3 rounded-lg border p-3">
                <RadioGroupItem value="all_funnel" id="exp-all" className="mt-0.5" />
                <Label htmlFor="exp-all" className="cursor-pointer font-normal leading-snug">
                  <span className="font-medium">Todos do funil</span>
                  <span className="block text-xs text-muted-foreground">
                    Exporta todos os {displayLeadTotal || "…"} leads de {funnelLabel}
                  </span>
                </Label>
              </div>

              <div className="flex items-start gap-3 rounded-lg border p-3">
                <RadioGroupItem value="stage" id="exp-stage" className="mt-0.5" />
                <div className="flex-1 space-y-2">
                  <Label htmlFor="exp-stage" className="cursor-pointer font-normal leading-snug">
                    <span className="font-medium">Por coluna do funil</span>
                    <span className="block text-xs text-muted-foreground">
                      Apenas leads de um estágio específico
                    </span>
                  </Label>
                  {scope === "stage" && (
                    <Select value={exportStageSlug} onValueChange={setExportStageSlug}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Estágio" />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => (
                          <SelectItem key={s.id} value={s.slug}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>
            </>
          )}

          {dataMode === "groups" && (
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="all_funnel" id="exp-group-all" className="mt-0.5" />
              <Label htmlFor="exp-group-all" className="cursor-pointer font-normal leading-snug">
                <span className="font-medium">Todos do grupo</span>
                <span className="block text-xs text-muted-foreground">
                  Exporta todos os leads de {groupLabel}
                </span>
              </Label>
            </div>
          )}

          {dataMode === "funnels" && hasActiveFilters && (
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="current_filter" id="exp-filter" className="mt-0.5" />
              <Label htmlFor="exp-filter" className="cursor-pointer font-normal leading-snug">
                <span className="font-medium">Filtro atual da tela</span>
                <span className="block text-xs text-muted-foreground">
                  Busca, estágio e tags aplicados ({loadedLeadCount} carregados — busca completa no servidor)
                </span>
              </Label>
            </div>
          )}

          {selectedCount > 0 && (
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="selected" id="exp-sel" className="mt-0.5" />
              <Label htmlFor="exp-sel" className="cursor-pointer font-normal leading-snug">
                <span className="font-medium">Selecionados</span>
                <span className="block text-xs text-muted-foreground">
                  {selectedCount} lead{selectedCount !== 1 ? "s" : ""} marcados na lista
                </span>
              </Label>
            </div>
          )}
        </RadioGroup>

        {exporting && progress > 0 && (
          <p className="text-sm text-muted-foreground">Carregando… {progress} leads</p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            Cancelar
          </Button>
          <Button onClick={() => void handleExport()} disabled={exporting}>
            {exporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Exportando…
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                Exportar{estimatedCount != null ? ` (${estimatedCount})` : ""}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
